import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../../ThemeContext";
import "./SimpleDatePicker.css";

const SimpleDatePicker = ({
  selectedDate,
  onChange,
  placeholder = "Select Date",
  disabled = false,
  className = "",
  dropdownPosition = "top", // "top" (default) or "bottom"
  dateFormat = "MM-DD-YYYY",
  style = {},
  allowPastDates = false,
  dropdownScale = 1,
  matchInputWidth = true,
  dropdownWidth = "100%",
  usePortal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());
  const { themeMode, currentTheme } = useTheme();
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const calendarRef = useRef(null);
  const [inputWidth, setInputWidth] = useState(undefined);
  const [portalPosition, setPortalPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setInputWidth(inputRef.current.offsetWidth);
    }
  }, [isOpen]);

  // Close calendar when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event) {
      const clickedInsideRoot = rootRef.current?.contains(event.target);
      const clickedInsideCalendar = calendarRef.current?.contains(event.target);
      if (!clickedInsideRoot && !clickedInsideCalendar) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !usePortal) return;

    const resolveWidth = (anchorWidth) => {
      if (matchInputWidth) return anchorWidth;
      if (typeof dropdownWidth === "number") return dropdownWidth;
      if (typeof dropdownWidth === "string") {
        const trimmed = dropdownWidth.trim();
        if (trimmed.endsWith("%")) {
          const percentage = Number.parseFloat(trimmed);
          if (!Number.isNaN(percentage)) {
            return (anchorWidth * percentage) / 100;
          }
        }
        if (trimmed.endsWith("px")) {
          const px = Number.parseFloat(trimmed);
          if (!Number.isNaN(px)) return px;
        }
      }
      return anchorWidth;
    };

    const updatePortalPosition = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      const width = resolveWidth(rect.width);
      const gap = 8;
      const viewportPadding = 8;
      const estimatedCalendarHeight = calendarRef.current?.offsetHeight || 340;

      let left = rect.left;
      if (left + width > window.innerWidth - viewportPadding) {
        left = window.innerWidth - viewportPadding - width;
      }
      if (left < viewportPadding) left = viewportPadding;

      let top =
        dropdownPosition === "bottom"
          ? rect.bottom + gap
          : rect.top - estimatedCalendarHeight - gap;

      if (top + estimatedCalendarHeight > window.innerHeight - viewportPadding) {
        top = Math.max(viewportPadding, rect.top - estimatedCalendarHeight - gap);
      }
      if (top < viewportPadding) {
        top = Math.max(viewportPadding, rect.bottom + gap);
      }

      setPortalPosition({ top, left, width });
    };

    updatePortalPosition();
    window.addEventListener("resize", updatePortalPosition);
    window.addEventListener("scroll", updatePortalPosition, true);
    return () => {
      window.removeEventListener("resize", updatePortalPosition);
      window.removeEventListener("scroll", updatePortalPosition, true);
    };
  }, [isOpen, usePortal, matchInputWidth, dropdownWidth, dropdownPosition]);

  const handleDateSelect = (date) => {
    onChange(date);
    setIsOpen(false);
  };

  // Create start of month by cloning viewDate to avoid timezone issues
  const startOfMonth = new Date(viewDate.getTime());
  startOfMonth.setDate(1);
  startOfMonth.setHours(12, 0, 0, 0); // Set to noon to avoid DST edge cases
  
  // Get days in month by going to next month day 0
  const endOfMonth = new Date(viewDate.getTime());
  endOfMonth.setDate(1);
  endOfMonth.setMonth(endOfMonth.getMonth() + 1);
  endOfMonth.setDate(0); // Day 0 of next month = last day of current month
  endOfMonth.setHours(12, 0, 0, 0);
  
  const startDay = startOfMonth.getDay();
  const daysInMonth = endOfMonth.getDate();

  const daysArray = [];
  for (let i = 0; i < startDay; i++) {
    daysArray.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    const dayDate = new Date(viewDate.getTime());
    dayDate.setDate(i);
    dayDate.setHours(12, 0, 0, 0); // Set to noon to avoid DST edge cases
    daysArray.push(dayDate);
  }

  const formatDate = (date) => {
    if (!date) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const separator = dateFormat.includes("/") ? "/" : "-";
    return `${month}${separator}${day}${separator}${year}`;
  };

  const isDateSelected = (date) => {
    if (!selectedDate || !date) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    if (allowPastDates) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    setViewDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(1); // Set to first day to avoid month overflow issues
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };
  const handleNextMonth = (e) => {
    e.stopPropagation();
    setViewDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(1); // Set to first day to avoid month overflow issues
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // Reset viewDate when opening or when selectedDate changes
  React.useEffect(() => {
    if (isOpen) {
      setViewDate(selectedDate || new Date());
    }
  }, [isOpen, selectedDate]);

  const calendarDropdown = (
    <div
      ref={calendarRef}
      className="calendar-dropdown"
      style={{
        background: currentTheme.callAutomationPage.dateBackground,
        borderRadius: "12px",
        boxShadow: "0 4px 24px rgba(255, 78, 58, 0.18)",
        padding: "18px",
        width: usePortal
          ? portalPosition.width || (matchInputWidth ? inputWidth || "100%" : dropdownWidth)
          : matchInputWidth
            ? inputWidth || "100%"
            : dropdownWidth,
        minWidth: 0,
        border: "none",
        transform: dropdownScale !== 1 ? `scale(${dropdownScale})` : "none",
        transformOrigin: dropdownPosition === "bottom" ? "top left" : "bottom left",
        zIndex: usePortal ? 2147483647 : undefined,
        ...(usePortal
          ? {
              position: "fixed",
              top: portalPosition.top,
              left: portalPosition.left,
              right: "auto",
              bottom: "auto",
            }
          : dropdownPosition === "bottom"
            ? { top: "110%", bottom: "auto" }
            : { bottom: "110%", top: "auto" }),
      }}
    >
      <div
        className="calendar-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          color: currentTheme.callAutomationPage.textColor,
          fontWeight: 600,
          fontSize: "1.1rem",
        }}
      >
        <button
          onClick={handlePrevMonth}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: currentTheme.callAutomationPage.textColor,
            fontSize: "1.2rem",
            padding: "0 8px",
          }}
        >
          &lt;
        </button>
        <span style={{ flex: 1, textAlign: "center" }}>
          {viewDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </span>
        <button
          onClick={handleNextMonth}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: currentTheme.callAutomationPage.textColor,
            fontSize: "1.2rem",
            padding: "0 8px",
          }}
        >
          &gt;
        </button>
      </div>

      <div
        className="calendar-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "6px",
          marginBottom: "6px",
        }}
      >
        {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
          <div
            key={`day-${index}`}
            style={{
              textAlign: "center",
              padding: "4px",
              fontSize: "13px",
              color: currentTheme.callAutomationPage.textColor,
              opacity: 0.7,
              fontWeight: 500,
            }}
          >
            {day}
          </div>
        ))}
        {daysArray.map((date, idx) => {
          if (!date) {
            return <div key={"empty-" + idx} />;
          }
          const isSelected = isDateSelected(date);
          const isDisabled = isDateDisabled(date);
          return (
            <div
              key={idx}
              onClick={() => !isDisabled && handleDateSelect(date)}
              style={{
                textAlign: "center",
                padding: "10px 0",
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.4 : 1,
                background: isSelected
                  ? currentTheme.callAutomationPage.activeButtonColor
                  : "transparent",
                color: isSelected
                  ? "#fff"
                  : currentTheme.callAutomationPage.textColor,
                borderRadius: "8px",
                fontWeight: isSelected ? 700 : 500,
                border: isSelected
                  ? `2px solid ${currentTheme.callAutomationPage.activeButtonColor}`
                  : "none",
                transition: "all 0.2s",
              }}
            >
              {date.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      className={`simple-date-picker ${className}`}
      style={{ position: "relative" }}
      ref={rootRef}
    >
      <div
        ref={inputRef}
        className={`picker-input ${themeMode}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          // background: currentTheme.callAutomationPage.dateBackground,
          color: currentTheme.callAutomationPage.textColor,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.7 : 1,
          padding: "10px 16px",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          border: "none",
          minWidth: "220px",
          fontWeight: 500,
          fontSize: "1rem",
          boxShadow: "none",
          gap: "10px",
          ...style,
        }}
      >
        <img
          src={
            themeMode === "dark"
              ? "/icons/calendar-dark.svg"
              : "/icons/calendar.svg"
          }
          alt="calendar"
          style={{
            width: "22px",
            height: "22px",
            minWidth: "22px",
            minHeight: "22px",
            marginRight: 8,
          }}
        />
        <span style={{ flex: 1 }}>
          {selectedDate ? formatDate(selectedDate) : placeholder}
        </span>
        <img
          src={
            isOpen
              ? process.env.PUBLIC_URL + "/icons/up-orange.png"
              : themeMode === "dark"
              ? process.env.PUBLIC_URL + "/icons/down.svg"
              : process.env.PUBLIC_URL + "/icons/down-black.png"
          }
          alt="toggle"
          style={{ width: "18px", height: "18px", marginLeft: 8 }}
        />
      </div>

      {isOpen &&
        !disabled &&
        (usePortal ? createPortal(calendarDropdown, document.body) : calendarDropdown)}
    </div>
  );
};

export default SimpleDatePicker;
